import React, { useState } from 'react';
import toast from 'react-hot-toast';
import {
    Table,
    Column,
    DataGrid,
} from '../Common';
import DropzoneModal from '../Common/DropzoneModal';
import Routes from '../../routes';
import DeleteIcon from '@mui/icons-material/Delete';
import GetAppIcon from '@mui/icons-material/GetApp';
import PropTypes from 'prop-types';
import UserInfo from '../../UserInfo';
import { select_commonCellProps, text_commonCellProps } from './DataGrid/utils';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import { getDateRangeFilterOperator } from './DataGrid/FilterComponents';
import { getFileIcon } from './Table/utils';
import StyledCellLink from './DataGrid/CellComponents/StyledCellLink';
import { IconButton, Tooltip } from '@mui/material';

const Documents = ({
    id,
    fileType,
    filters,
    showTableOnly,
    openModal,
    closeModal,
    fromPage,
    showAddButton,
    hideExportButton,
    hideActions
}) => {
    const [isSaving, setIsSaving] = useState(false);
    const [dropzoneOpen, setDropzoneOpen] = useState(false);
    const [file, setFile] = useState([]);
    const [fileTypeId, setFileTypeId] = useState(fileType);
    const [refId, setRefId] = useState(id);
    const [fileNotes, setFileNotes] = useState('');
    const [dataRefresh, setDataRefresh] = useState(false);

    const addDocument = () => setDropzoneOpen(true);

    const closeDropzoneModal = showTableOnly ? () => closeModal() : () => setDropzoneOpen(false);

    const handleSubmit = async () => {

        if (!file && !file.length <= 0) {
            return toast.error('File not found', { duration: 5000 });
        }

        try {
            setIsSaving(true);

            const files = file.map(x => ({
                refId: refId,
                fileTypeId: fileTypeId,
                notes: fileNotes,
                fileBytes: x.data,
                fileName: x.file.name,
                fileExt: `.${x.file.name.split('.').pop()}`,
                contentType: x.file.type
            }));

            const response = await fetch(Routes.File.UploadFile(), {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    files
                }),
            });

            setIsSaving(false);
            if (response.status === 200) {
                closeDropzoneModal();
                setRefId(id);
                setFileTypeId(fileType);
                setFileNotes("");
                setFile([]);
                toast.success('The file has been successfully uploaded.', { duration: 5000 });
                return setDataRefresh(prev => !prev);
            }
        }
        catch {
            setIsSaving(false);
            toast.error('Error Uploading File', { duration: 5000 });
        }
        finally {
            setIsSaving(false);
        }
    };

    const deleteFile = async (row) => {
        console.log(row);
        fetch("api/Files/DeleteFile", {
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            method: 'POST',
            body: JSON.stringify(row),
        })
            .then(() => {
                toast.success('The file has been successfully deleted.', { duration: 5000 });
                return setDataRefresh(prev => !prev);
            })
            .catch(err => console.log(err));
    };

    const downloadFile = async (row) => {
        const downloadURL = 'api/Files/GetFileLink';
        fetch(downloadURL, {
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            method: 'POST',
            body: JSON.stringify(row),
        })
            .then(async (response) => {
                const URL = await Promise.resolve(response.json());
                console.log({
                    URL
                })
                window.open(URL, '_blank').focus();
            })
            .catch(err => console.log(err));
    };

    const canUserDelete = (file) => {
        const isSystemOwnerSuperTenantAdminOrTenantAdmin = (UserInfo.isSuperTenantAdmin() || UserInfo.isTenantAdministrator()) && UserInfo.isSystemOwner() && UserInfo.isNativeUser();
        const isServiceProviderTenantAdmin = UserInfo.isTenantAdministrator() && UserInfo.isServiceProvider() && UserInfo.isNativeUser();

        if (UserInfo.isSystemAdministrator() || isSystemOwnerSuperTenantAdminOrTenantAdmin) {
            return true;
        }
        if (isServiceProviderTenantAdmin) {
            return file?.fileTypeId === 2;
        }
        return false;
    };

    const canUserAdd = () => {
        const isSystemOwnerRoleWithAddPermission = 
        (
            UserInfo.isSuperTenantAdmin() ||
            UserInfo.isTenantAdministrator() ||
            UserInfo.isRegionalAdministrator() ||
            UserInfo.isTechnicianManager() 
        )
            && UserInfo.isSystemOwner()
            && UserInfo.isNativeUser();

        const isNonNativeContractorLiteRoleWithAddPermission =
        (
            UserInfo.isTenantAdministrator() ||
            UserInfo.isTechnicianManager() ||
            UserInfo.isTechnician()
        )
            && UserInfo.isNativeAccountServiceProvider();

        return UserInfo.isSystemAdministrator() || isSystemOwnerRoleWithAddPermission || isNonNativeContractorLiteRoleWithAddPermission;
    };

    const tableActions = (canUserAdd() && !hideActions) ? {
        MUIButtonText: "Add Document",
        MUIButtonOnClick: () => addDocument(),
    } : {
        filterOptions: filters
    };

    const dateRangeFilterOperator = getDateRangeFilterOperator({});
    const columns = [
        {
            field: 'fileName',
            headerName: 'Document Name',
            ...text_commonCellProps,
            renderCell: (params) => (
                <>
                    <img
                        src={getFileIcon(params.row.fileName)}
                        alt="file icon"
                        width="20"
                        height="20"
                        className="file-icon"
                    />
                    <StyledCellLink
                        text={params.value}
                        onClick={() => downloadFile(params.row)}
                        primaryLink
                    />
                </>
            ),
        },
        {
            field: 'fileTypeName',
            headerName: 'Shared With',
            ...select_commonCellProps,
            hidden: !showTableOnly,
        },
        {
            field: 'createDate',
            headerName: 'Date',
            filterOperators: dateRangeFilterOperator,
            flex: 1,
            type: 'datetime',
            valueGetter: (date) => date,
            valueFormatter: (date) => {
                date = new Date(date);
                return date.toLocaleDateString('en-GB')
            },
        },
        {
            field: 'fullName',
            headerName: 'Owner',
            ...text_commonCellProps,
        },
        {
            field: 'notes',
            headerName: 'Notes',
            ...text_commonCellProps,
        },
        {
            field: 'Quick Actions',
            headerName: '',
            type: 'actions',
            width: 100,
            getActions: (params) => {
                const actions = [];
                const pushEmptyIcon = () =>
                    actions.push(
                        <IconButton
                            disabled
                            sx={{ width: "30px", height: "30px", padding: "0px", margin: "0px" }}
                        />
                    );
                if (canUserDelete(params.row)) {
                    actions.push(
                        <GridActionsCellItem
                            icon={
                                <Tooltip title="Delete File">
                                    <DeleteIcon />
                                </Tooltip>
                            }
                            label="Delete"
                            onClick={() => deleteFile(params.row)}
                            showInMenu={false}
                        />
                    );
                } else {
                    pushEmptyIcon();
                }
                actions.push(
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="Download File">
                                <GetAppIcon />
                            </Tooltip>
                        }
                        label="Download"
                        onClick={() => downloadFile(params.row)}
                        showInMenu={false}
                    />
                );
                return actions;
            },

        },
    ];

    return (
        <>
            <DataGrid
                url={Routes.File.FileList(fileType, id)}
                refresh={dataRefresh}
                tableHeader='Documents'
                filterOptions={filters}
                noResultText="Oops... It is empty here..."
                noResultText2="Please add a document to get started"
                {...tableActions}
                columns={columns}
                hideExportButton={hideExportButton}
                showAddButton={showAddButton}
            />

            <DropzoneModal
                showDocumentPageField={showTableOnly}
                dropzoneOpen={dropzoneOpen || openModal}
                fileTypeId={fileTypeId}
                refId={refId}
                fileNotes={fileNotes}
                selectedFiles={file}
                isSaving={isSaving}
                setRefId={setRefId}
                setFileTypeId={setFileTypeId}
                setDropzoneFiles={setFile}
                setFileNotes={setFileNotes}
                handleSubmit={handleSubmit}
                handleClose={closeDropzoneModal}
            />
        </>
    );
};

Documents.propTypes = {
    id: PropTypes.string.isRequired,
    fileType: PropTypes.number.isRequired,
    showTableOnly: PropTypes.bool,
    filters: PropTypes.array,
    openModal: PropTypes.bool,
    closeModal: PropTypes.func,
    showAddButton: PropTypes.bool,
    hideActions: PropTypes.bool
};

Documents.defaultProps = {
    showTableOnly: false,
    filters: [],
    openModal: false,
    closeModal: () => {},
    hideExportButton: true,
    showAddButton: false,
    hideActions: false
};

export default Documents;
